import { CustomUserTimezone, TimeRangeInterface } from '@twaice-fe/shared/models';

export function utcStartOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

export function utcEndOfDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999));
}

export function utcStartOfDayUnix(date: Date): number {
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0) / 1000);
}

export function utcEndOfDayUnix(date: Date): number {
  return Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999) / 1000);
}

/**
 * Returns the UTC offset in milliseconds for the given IANA timezone at the given instant.
 * Positive means the timezone is ahead of UTC.
 */
const offsetFmtCache = new Map<string, Intl.DateTimeFormat>();

function getOffsetFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = offsetFmtCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    offsetFmtCache.set(timeZone, fmt);
  }
  return fmt;
}

export function getOffsetMs(tz: string, date: Date): number {
  const val = (parts: Intl.DateTimeFormatPart[], type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  const toMs = (parts: Intl.DateTimeFormatPart[]) =>
    Date.UTC(
      val(parts, 'year'),
      val(parts, 'month') - 1,
      val(parts, 'day'),
      val(parts, 'hour'),
      val(parts, 'minute'),
      val(parts, 'second')
    );
  return toMs(getOffsetFormatter(tz).formatToParts(date)) - toMs(getOffsetFormatter('UTC').formatToParts(date));
}

export function getTimezoneOffsetFromUtc(timezone: string): string {
  const tzDate = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
  const utcDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'UTC' }));
  const offset = tzDate.getTime() - utcDate.getTime();
  if (offset === 0) return 'UTC';
  const sign = offset > 0 ? '+' : '-';
  const totalMinutes = Math.abs(offset / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export interface TimezoneListOption {
  name: string;
  offset?: string;
  isGroupDivider: boolean;
  value: string;
}

/**
 * All timezones the browser's ICU data knows about, sorted alphabetically and grouped by region
 * with non-selectable divider rows — the same list and grouping as the user's timezone display
 * preference (Settings > Date & time), reused wherever a picker needs a literal IANA timezone.
 */
interface IntlWithSupportedValuesOf {
  supportedValuesOf(input: string): string[];
}

export function buildTimezoneOptions(): TimezoneListOption[] {
  const availableTimezones = (Intl as unknown as IntlWithSupportedValuesOf).supportedValuesOf('timeZone');
  availableTimezones.sort((a, b) => a.localeCompare(b));

  const options: TimezoneListOption[] = [
    { name: availableTimezones[0].split('/')[0], isGroupDivider: true, value: availableTimezones[0] },
  ];

  availableTimezones.forEach((timezone, index) => {
    options.push({
      name: timezone.replace('_', ' '),
      offset: getTimezoneOffsetFromUtc(timezone),
      isGroupDivider: false,
      value: timezone,
    });

    if (availableTimezones[index + 1] && availableTimezones[index + 1].split('/')[0] !== timezone.split('/')[0]) {
      options.push({
        name: availableTimezones[index + 1].split('/')[0],
        isGroupDivider: true,
        value: availableTimezones[index + 1],
      });
    }
  });

  return options;
}

export function isInvalidDownloadTimeRange(timeRange: TimeRangeInterface, maxDownloadDays: number): boolean {
  const [startDate, endDate] = timeRange?.value ?? [];
  if (!startDate || !endDate) return true;
  const deltaSeconds = (new Date(endDate as string | Date).getTime() - new Date(startDate as string | Date).getTime()) / 1000;
  return deltaSeconds > maxDownloadDays * 24 * 3600;
}

/**
 * Resolves the effective IANA timezone string from the user's preference.
 *
 * @param userTimezone  The raw value stored in the user profile (a CustomUserTimezone enum or an IANA string).
 * @param systemTimezone  The storage-level timezone of the currently selected system (may be null).
 * @returns An IANA timezone string, or null when nothing is configured.
 */
export function resolveTimezone(userTimezone: string | null, systemTimezone: string | null): string | null {
  switch (userTimezone) {
    case CustomUserTimezone.BROWSER_TIME:
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    case CustomUserTimezone.UTC:
      return 'UTC';
    case CustomUserTimezone.STORAGE_TIME:
      return systemTimezone;
    default:
      return userTimezone || null;
  }
}
